import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, firstValueFrom } from 'rxjs';
import type { SelfProfile, LinkedAccount, UpdateProfile, ChangePassword, SetPassword } from '@zag/auth/models';
import { LanguageService, ThemeService, type Theme } from '@zag/ui';
import { type SupportedLanguage } from '@zag/core/common';

export type { SelfProfile, LinkedAccount, UpdateProfile, ChangePassword, SetPassword } from '@zag/auth/models';

/**
 * Frontend service for the authenticated user's own profile, preferences, security, and linked accounts.
 *
 * Provides reactive state via rxResource and imperative mutation methods.
 */
@Injectable({ providedIn: 'root' })
export class UsersMeService {
  private readonly http = inject(HttpClient);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly basePath = '/api/v1/users/me';

  // ============================================================================
  // Profile Resource
  // ============================================================================

  private readonly profileRefreshTrigger = signal(0);

  private readonly profileResource = rxResource<SelfProfile, void>({
    params: () => ({ _trigger: this.profileRefreshTrigger() }),
    stream: () => this.http.get<SelfProfile>(`${this.basePath}/profile`),
  });

  // Guard against the 'error' status: calling .value() on an errored rxResource
  // throws ResourceValueError. Return undefined instead so callers (e.g. computed
  // signals in route providers) can handle a missing profile gracefully.
  readonly profile = () => (this.profileResource.status() === 'error' ? undefined : this.profileResource.value());
  // Exposed as the raw Signal (not a wrapper function) so guards can pass it to toObservable().
  readonly profileIsLoading = this.profileResource.isLoading;
  readonly profileError = () => this.profileResource.error();

  reloadProfile(): void {
    this.profileRefreshTrigger.update(v => v + 1);
  }

  // ============================================================================
  // Accounts Resource
  // ============================================================================

  private readonly accountsRefreshTrigger = signal(0);

  private readonly accountsResource = rxResource<LinkedAccount[], void>({
    params: () => ({ _trigger: this.accountsRefreshTrigger() }),
    stream: () => this.http.get<LinkedAccount[]>(`${this.basePath}/accounts`),
  });

  readonly accounts = () => this.accountsResource.value() ?? [];
  readonly accountsIsLoading = () => this.accountsResource.isLoading();

  reloadAccounts(): void {
    this.accountsRefreshTrigger.update(v => v + 1);
  }

  // ============================================================================
  // Mutations
  // ============================================================================

  /**
   * Update the current user's profile (name, image, preferredLanguage, preferredTheme).
   */
  updateProfile(data: UpdateProfile): Observable<void> {
    return this.http.patch<void>(`${this.basePath}/profile`, data);
  }

  /**
   * Change the current user's password (requires current password).
   */
  changePassword(data: ChangePassword): Observable<void> {
    return this.http.post<void>(`${this.basePath}/security/password-changes`, data);
  }

  /**
   * Set a password for a social-only account (no current password needed).
   */
  setPassword(data: SetPassword): Observable<void> {
    return this.http.put<void>(`${this.basePath}/security/password`, data);
  }

  /**
   * Unlink an authentication account by providerId.
   */
  unlinkAccount(providerId: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/accounts/${encodeURIComponent(providerId)}`);
  }

  /**
   * Apply the active user's language to translations, then fetch the user's server-side preferences.
   * Called after every identity change (session restore, sign-in).
   * Best-effort — failures are silently ignored.
   */
  async syncServerPreferences(): Promise<void> {
    this.languageService.applyCurrentLanguage();
    this.themeService.applyCurrentTheme();

    try {
      const profile = await firstValueFrom(this.http.get<SelfProfile>(`${this.basePath}/profile`));
      const { preferredLanguage, preferredTheme } = profile;

      if (preferredLanguage && !this.languageService.hasStoredLanguage()) {
        this.languageService.setLanguage(preferredLanguage as SupportedLanguage);
      }
      if (preferredTheme && !this.themeService.hasStoredTheme()) {
        this.themeService.setTheme(preferredTheme as Theme);
      }
    } catch {
      // Best-effort: if the user has no profile permissions yet, this silently fails
    }
  }
}
